import './index.css'
import { CHANNELS_UNLIMITED } from '@disco/constants/FeatureTypes'
import {
  CUSTOM_CHANNEL,
  MAX_CHANNELS_LIMITED_MODE,
} from '@disco/constants/GroupTypes'
import type { CHANNEL_SORT } from '@disco/constants/SortTypes'
import { loadLibrary } from '@disco/state-management/actions/ExplorerActions'
import {
  createGroup,
  deleteGroup,
  updateGroup,
} from '@disco/state-management/actions/GroupActions'
import { setChannelsSearchTerm } from '@disco/state-management/actions/SearchActions'
import { makeGetFeatureData } from '@disco/state-management/selectors/business'
import {
  getGroupsCount,
  isGroupsExcessLimit,
} from '@disco/state-management/selectors/groups'
import {
  getChannelsSortParam,
  getUser,
} from '@disco/state-management/selectors/user'
import { debounce } from 'lodash'
import * as React from 'react'
import { MdAddCircle } from '@react-icons/all-files/md/MdAddCircle'
import { connect } from 'react-redux'
import type { Group, GroupId } from 'types/group'
import type { User } from 'types/user'
import GroupModal from 'components/common/custom-libraries/edit-form'
import { Flex, IconButton, SearchTextField } from 'components/ds'
import { CustomLibrariesModal } from 'components/modals/custom-libraries-modal'
import PricingBadge from 'components/ui/pricing-badge'
import Tooltip from 'components/ui/tooltip'
import ChannelSortDropdown from './custom-libraries/channel-sort-dropdown'
import type { RootState } from 'store/configureStore'
const MODAL_LOCAL_STORAGE_KEY = 'CustomLibrariesList_BannerDescription'
// TODO: move declaration of `type Explorer` upper
type Explorer = {
  panes: {
    groupId: GroupId | null | undefined
  }[]
}

class CustomLibrariesList extends React.Component<
  {
    channelSort: CHANNEL_SORT
    explorer: Explorer
    groupsCount: number
    user: User
    loadLibrary: typeof loadLibrary
    updateGroup: typeof updateGroup
    createGroup: typeof createGroup
    deleteGroup: typeof deleteGroup
    setChannelsSearchTerm: typeof setChannelsSearchTerm
    channelsLimited: boolean
    isTrial: boolean
    badgeTitle: boolean
    groups: Record<string, any>
    groupsLoaded: boolean
    isExcessLimit: boolean
    hideSearch?: boolean
  },
  {
    showNewGroupModal: boolean
    showNewSubfolderModal: boolean
    currentGroup: Group | null | undefined
    showUpgradeMoreModal: boolean
    searchTerm: string
  }
> {
  state = {
    showNewGroupModal: false,
    showNewSubfolderModal: false,
    currentGroup: null,
    showUpgradeMoreModal: false,
    searchTerm: '',
  }
  _handleGroupClick = (group: Group) => this.props.loadLibrary(group)
  _handleNewGroupClick = () => {
    const dontShowUpgradeMoreModal = window.localStorage.getItem(
      MODAL_LOCAL_STORAGE_KEY,
    )

    if (
      !this.props.isExcessLimit ||
      (dontShowUpgradeMoreModal && this.props.isTrial)
    ) {
      this.setState({
        showNewGroupModal: true,
      })
      return
    }

    if (this.props.isExcessLimit) {
      this.setState({
        showUpgradeMoreModal: true,
      })
    } else {
      this.setState({
        showNewGroupModal: true,
      })
    }
  }
  _onCloseUpgradeMoreModal = () => {
    this.setState({
      showUpgradeMoreModal: false,
    })
  }
  _handleNewSubfolderClick = (group: Group) =>
    this.setState({
      currentGroup: group,
      showNewSubfolderModal: true,
    })
  _onCloseModal = () =>
    this.setState({
      showNewGroupModal: false,
      showNewSubfolderModal: false,
      currentGroup: null,
    })
  _debounceUpdateUserChannelSearchTerm = debounce((input?: string) => {
    const searchTerm = input || ''
    this.props.setChannelsSearchTerm(searchTerm)
  }, 50)
  _handleInputChange = (value: string) => {
    this.setState({
      searchTerm: value,
    })

    this._debounceUpdateUserChannelSearchTerm(value)
  }

  componentDidMount() {
    window.addEventListener(
      'load',
      this.props.setChannelsSearchTerm(this.state.searchTerm),
    )
  }

  render() {
    const { channelsLimited, isTrial, badgeTitle, groupsLoaded, hideSearch } =
      this.props
    const dontShowUpgradeMoreModal = window.localStorage.getItem(
      MODAL_LOCAL_STORAGE_KEY,
    )
    let newGroupModal: React.ReactNode = null

    if (this.state.showNewGroupModal) {
      newGroupModal = (
        <GroupModal
          groupType={CUSTOM_CHANNEL}
          onSubmit={this._onCloseModal}
          onClose={this._onCloseModal}
        />
      )
    }

    return (
      <div className="Channels">
        <div className="Sidebar_BaseItem Sidebar_Title">
          <div className="Sidebar_Label">Channels</div>
          <div className="Sidebar_ChanAction">
            <Tooltip
              content={
                this.props.isExcessLimit ? (
                  <div className="CustomLibraries_LimitedTooltip">
                    Want more than {MAX_CHANNELS_LIMITED_MODE}
                    <br />
                    channels? <br />
                    <PricingBadge title={badgeTitle} />
                  </div>
                ) : (
                  'Add new channel'
                )
              }
            >
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={groupsLoaded ? this._handleNewGroupClick : undefined}
                aria-label="Add channel"
              >
                <MdAddCircle />
              </IconButton>
            </Tooltip>
          </div>
          {newGroupModal}
          {(channelsLimited || (isTrial && !dontShowUpgradeMoreModal)) && (
            <CustomLibrariesModal
              onCloseUpgradeMoreModal={this._onCloseUpgradeMoreModal}
              showUpgradeMoreModal={this.state.showUpgradeMoreModal}
              isTrial={isTrial}
              onOpenCreateGroupModal={() => {
                this.setState({
                  showNewGroupModal: true,
                  showUpgradeMoreModal: false,
                })
              }}
              id={MODAL_LOCAL_STORAGE_KEY}
            />
          )}
        </div>
        {!hideSearch && (
          <div className="Sidebar_Search">
            <Flex gap="3" align="center">
              <SearchTextField
                value={this.state.searchTerm}
                onChange={e => this._handleInputChange(e.target.value)}
                theme="dark"
              />
              <ChannelSortDropdown />
            </Flex>
          </div>
        )}
      </div>
    )
  }
}

export default connect(
  (state: RootState) => {
    const getFeatureData = makeGetFeatureData()
    const { isFeatureEnabled, isTrial, badgeTitle } = getFeatureData(state, {
      featureName: CHANNELS_UNLIMITED,
    })
    return {
      ...state.groups,
      explorer: state.explorer,
      groupsCount: getGroupsCount(state),
      user: getUser(state),
      channelSort: getChannelsSortParam(state),
      channelsLimited: !isFeatureEnabled,
      isTrial,
      badgeTitle,
      groupsLoaded: state.groups.isFetched,
      isExcessLimit: isGroupsExcessLimit(state),
    }
  },
  {
    loadLibrary,
    updateGroup,
    createGroup,
    deleteGroup,
    setChannelsSearchTerm,
  },
  // @ts-expect-error type connector
)(CustomLibrariesList) as any
